import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react'
import {
  A4DpiToDetailsMap,
  FOOTER_TYPES,
  PRESCRIPTION_BUILDER_VIEW_TYPES,
  PRESCRIPTION_LAYOUTS,
  PRESCRIPTION_PRINT_TYPES,
} from 'src/consumer/constants'
import { Box } from '@mui/material'
import useElementDimensions from 'src/customHooks/customHooks'
import usePrintableSectionsConfig from 'src/mentor/components/PrescriptionMakingPage/printComponents/hooks/usePrintableSectionsConfig'
import ImageWithMenu from 'src/mentor/components/PrescriptionMakingPage/printComponents/prescriptionImageRender/components/ImageWithMenu'
import PrescriptionHeaderBodyFooterDebugLabels from 'src/mentor/components/PrescriptionMakingPage/printComponents/printComponents/debugComponents/PrescriptionHeaderBodyFooterDebugLabels'
import PatientDetails from 'src/mentor/components/PrescriptionMakingPage/printComponents/printComponents/prescriptionPrintableItems/PatientDetails'
import { PrescriptionFooter } from '../PrescriptionMakingPageComponents'
import { PMPConsultationParent, PMPV2FooterContainer } from '../PrescriptionMakingPageStyles'
import A4PreviewWrapper from './A4PreviewWrapper'
import OldPrescriptionViewComponent from './OldPrescriptionViewComponent'
import PrescriptionFullBody from './printComponents/PrescriptionFullBody'
import PrescriptionPrintableBody from './printComponents/PrescriptionPrintableBody'
import PrescriptionPrintableFooter from './printComponents/PrescriptionPrintableFooter'
import PrescriptionPrintableHeader, { PrescriptionHeaderRender } from './printComponents/PrescriptionPrintableHeader'
import ContinuedToAndFromDiv from './printComponents/prescriptionPrintableItems/ContinuedToAndFromDiv'
import PrintablePatientHistoryNotes from './printComponents/prescriptionPrintableItems/PrintablePatientHistoryNotes'
import PrescriptionSectionErrorBoundary from './printComponents/PrescriptionSectionErrorBoundary'
import { A4PageBackgroundContainer, A4WrapperContainer } from './styledComponents'
import {
  A4_DIMENSIONS_IN_INCHES,
  convertInchesToPixels,
  getA4DimensionsInPixels,
  getPrescriptionItemRenderingList,
  PRESCRIPTION_ITEM_ORDER_IDS,
  checkAndRenderBasedOnEmrSections,
  checkShowInPrintForEmrSection,
} from './utils'
import { EMR_SECTION_IDS } from 'src/mentor/components/MentorPrescriptionTemplates/components/utils/emrSectionIds'

export default function PrescriptionViewComponent({
  printSettings,
  prescriptionData: prescData,
  prescriptionViewSettings,
  printType,
  debugMode,
  drugsAdvice,
  selectedAdvice,
  selectedTherapies,
  selectedInstructions,
  selectedInvestigations,
  selectedObservations,
  selectedAssessments,
  selectedComplaints,
  selectedDiagnosis,
  vital,
  jointHomunculusData,
  suggestedBookingTherapy,
  emrSections,
  value,
  setValue,
  placeholder = 'Enter prescription...',
  honorPropValue,
  setPropValue,
  nextVisitInputType,
  nextVisitInputValue,
  prescriptionTemplateData,
  finalRequestedTestsBy,
  oldUIDrugSubSections,
  referredToDoctorName,
  showInUI,
  refreshDimensionsDependencies = [],
  useOldUI = false,
  patientHistoryNotes,
  freeFormRichText,
  useFullUrlForHeaderImages = false,
  useFullUrlForFooterImages = false,
  showTheA4Page = false,
  onTotalPagesChange,
  colorMode = 'colored', // 'colored' or 'bw'
  useEmptyBox = false,
  useBestFit = false,
  practitionerId, // Optional: required for support portal
}) {
  const rectangleRefs = useRef({})
  
  const prescriptionData = useMemo(() => {
    if (prescriptionTemplateData) {
      return {
        ...prescriptionTemplateData,
        prescriptionData: prescData?.prescriptionData,
        patientPrefill: prescData?.patientPrefill,
      }
    }
    return prescData
  }, [prescData, prescriptionTemplateData])

  const { styleOverrides } = usePrintableSectionsConfig({
    emrSections,
    prescriptionViewSettings,
    prescriptionTemplateData,
  })

  // Color scheme utility function - returns professional, modern colors for text
  const getSectionColor = useCallback((sectionKey, colorType = 'color') => {
    if (colorMode === 'bw') {
      // B&W mode: return black for text
      return '#000000'
    } else {
      // Colored mode: return professional, modern colors for text
      const professionalColors = {
        patientDetails: { color: '#1e293b', headingColor: '#0f172a' }, // Slate
        vitals: { color: '#1e293b', headingColor: '#0f172a' }, // Slate
        diagnosis: { color: '#1e40af', headingColor: '#1e3a8a' }, // Blue
        observations: { color: '#dc2626', headingColor: '#b91c1c' }, // Red
        complaints: { color: '#059669', headingColor: '#047857' }, // Emerald
        drugAdvice: { color: '#0369a1', headingColor: '#075985' }, // Sky Blue
        homunculus: { color: '#d97706', headingColor: '#b45309' }, // Amber
        advice: { color: '#7c3aed', headingColor: '#6d28d9' }, // Violet
        investigations: { color: '#c026d3', headingColor: '#a21caf' }, // Fuchsia
        selectedTherapy: { color: '#2563eb', headingColor: '#1d4ed8' }, // Blue
        suggestedTherapy: { color: '#ea580c', headingColor: '#c2410c' }, // Orange
        instructions: { color: '#dc2626', headingColor: '#b91c1c' }, // Red
        referredToDoctorName: { color: '#64748b', headingColor: '#475569' }, // Slate
        header: { color: '#1e293b', headingColor: '#0f172a' }, // Slate
        footer: { color: '#1e293b', headingColor: '#0f172a' }, // Slate
        nextVisitAndSignature: { color: '#1e293b', headingColor: '#0f172a' }, // Slate
        bodyColor: { color: '#1e293b', headingColor: '#0f172a' }, // Slate
        patientHistoryNotes: { color: '#1e293b', headingColor: '#0f172a' }, // Slate
      }
      return professionalColors[sectionKey]?.[colorType] || '#1e293b'
    }
  }, [colorMode])

  // Enhanced styleOverrides with color mode support
  const enhancedStyleOverrides = useMemo(() => {
    // Map EMR section IDs to color keys and their order indices
    // Order based on typical prescription layout (for alternate border application)
    // Using EMR_SECTION_IDS constants as keys since styleOverrides uses these
    const sectionOrderMap = {
      [EMR_SECTION_IDS.PATIENT_VITALS]: { colorKey: 'vitals', orderIndex: 0 },
      [EMR_SECTION_IDS.OBSERVATIONS]: { colorKey: 'observations', orderIndex: 1 },
      [EMR_SECTION_IDS.HEALTH_COMPLAINTS]: { colorKey: 'complaints', orderIndex: 2 },
      [EMR_SECTION_IDS.DIAGNOSIS]: { colorKey: 'diagnosis', orderIndex: 3 },
      [EMR_SECTION_IDS.DRUG_ADVICE]: { colorKey: 'drugAdvice', orderIndex: 4 },
      [EMR_SECTION_IDS.ADVICE]: { colorKey: 'advice', orderIndex: 5 },
      [EMR_SECTION_IDS.INVESTIGATIONS]: { colorKey: 'investigations', orderIndex: 6 },
      [EMR_SECTION_IDS.PSYCH_ASSESSMENTS]: { colorKey: 'selectedTherapy', orderIndex: 7 },
      [EMR_SECTION_IDS.PATIENT_HISTORY_NOTES]: { colorKey: 'patientHistoryNotes', orderIndex: 8 },
      [EMR_SECTION_IDS.THERAPY_RECOMMENDATION]: { colorKey: 'selectedTherapy', orderIndex: 9 },
      [EMR_SECTION_IDS.JOINT_HOMUNCULUS]: { colorKey: 'homunculus', orderIndex: 10 },
      [EMR_SECTION_IDS.INSTRUCTIONS]: { colorKey: 'instructions', orderIndex: 11 },
      [EMR_SECTION_IDS.REFER_TO]: { colorKey: 'referredToDoctorName', orderIndex: 12 },
      [EMR_SECTION_IDS.NEXT_VISIT_DETAILS]: { colorKey: 'nextVisitAndSignature', orderIndex: 13 },
      [EMR_SECTION_IDS.SIGNATURE]: { colorKey: 'nextVisitAndSignature', orderIndex: 14 },
    }
    
    // Start with existing styleOverrides if present
    const enhanced = styleOverrides ? { ...styleOverrides } : {}
    
    // Only add colors if styleOverrides don't already have them
    Object.keys(sectionOrderMap).forEach(sectionId => {
      const { colorKey, orderIndex } = sectionOrderMap[sectionId]
      if (colorKey) {
        const existingOverrides = enhanced[sectionId] || {}
        
        // Apply border only to alternate sections (even indices: 0, 2, 4, 6...) in colored mode
        const shouldHaveBorder = colorMode === 'colored' && 
                                 !existingOverrides.borderColor && 
                                 orderIndex >= 0 && 
                                 orderIndex % 2 === 0
        
        // Only add colors if they're not already set in styleOverrides
        enhanced[sectionId] = {
          ...existingOverrides,
          // Don't add backgroundColor - keep backgrounds clean
          // Add subtle left border for visual distinction (only on alternate sections in colored mode)
          ...(shouldHaveBorder && {
            borderLeft: `3px solid ${getSectionColor(colorKey, 'headingColor')}`,
            paddingLeft: existingOverrides.paddingLeft || '12px',
          }),
          // Add text colors only if not already set
          ...(!existingOverrides.color && {
            color: getSectionColor(colorKey, 'color'),
          }),
          // Add heading colors only if not already set
          ...(!existingOverrides.headingColor && {
            headingColor: getSectionColor(colorKey, 'headingColor'),
          }),
        }
      }
    })
    
    return enhanced
  }, [styleOverrides, colorMode, getSectionColor])

  const currentDpiData = A4DpiToDetailsMap?.[printSettings?.dpi]
  const dpi = currentDpiData?.id

  // Get A4 dimensions in pixels based on DPI
  const a4Dimensions = getA4DimensionsInPixels(dpi)
  const heightOfA4Page = a4Dimensions.height
  const widthOfA4Page = a4Dimensions.width

  let heightOfPrescription = heightOfA4Page - 0.5
  let prescriptionWidth = widthOfA4Page
  let leftMarginOffsetInPx = 0
  let rightMarginOffsetInPx = 0
  let topMarginOffsetInPx = 0
  let bottomMarginOffsetInPx = 0

  // Convert margin values from inches to pixels
  if (printSettings?.topMargin) {
    topMarginOffsetInPx = convertInchesToPixels(printSettings?.topMargin, dpi)
    heightOfPrescription = heightOfPrescription - topMarginOffsetInPx
  }
  if (printSettings?.bottomMargin) {
    bottomMarginOffsetInPx = convertInchesToPixels(printSettings?.bottomMargin, dpi)
    heightOfPrescription = heightOfPrescription - bottomMarginOffsetInPx
  }

  if (printSettings?.leftMargin) {
    leftMarginOffsetInPx = convertInchesToPixels(printSettings?.leftMargin, dpi)
    prescriptionWidth = prescriptionWidth - leftMarginOffsetInPx
  }
  if (printSettings?.rightMargin) {
    rightMarginOffsetInPx = convertInchesToPixels(printSettings?.rightMargin, dpi)
    prescriptionWidth = prescriptionWidth - rightMarginOffsetInPx
  }

  // const a4PageWidth = 559
  // above dimensions are being done in 96 dpi

  const {
    dimensions: a4PageDimensions,
    ref: a4PageRef,
    refresh: a4PageDimensionsRefresh,
  } = useElementDimensions()
  const {
    dimensions: headerDimensions,
    ref: headerRef,
    refresh: headerDimensionsRefresh,
  } = useElementDimensions()
  const { dimensions: _prescriptionContinuousBodyDimensions, ref: prescriptionContinuousBodyRef } =
    useElementDimensions()
  const {
    dimensions: footerDimensions,
    ref: footerRef,
    refresh: footerDimensionsRefresh,
  } = useElementDimensions()

  // prescription items dimensions
  const {
    dimensions: patientDetailsDimensions,
    ref: patientDetailsRef,
    refresh: patientDetailsDimensionsRefresh,
  } = useElementDimensions()
  const {
    dimensions: vitalsDimensions,
    ref: vitalsRef,
    refresh: refreshVitalDimensions,
  } = useElementDimensions()
  const {
    dimensions: observationDimensions,
    ref: observationRef,
    refresh: observationsDimenstionsRefresh,
  } = useElementDimensions()
  const {
    dimensions: complaintsDimensions,
    ref: complaintsRef,
    refresh: complaintsDimensionsRefresh,
  } = useElementDimensions()
  const {
    dimensions: diagnosisDimensions,
    ref: diagnosisRef,
    refresh: diagnosisDimensionsRefresh,
  } = useElementDimensions()
  const {
    dimensions: drugAdviceTableDimensions,
    ref: drugAdviceTableRef,
    refresh: drugAdviceDimenstionsRefresh,
  } = useElementDimensions()
  const {
    dimensions: prescriptionAdviceDimensions,
    ref: prescriptionAdviceRef,
    refresh: prescriptionAdviceDimensionsRefresh,
  } = useElementDimensions()
  const {
    dimensions: investigationsDimensions,
    ref: investigationsRef,
    refresh: investigationsDimensionsRefresh,
  } = useElementDimensions()
  const {
    dimensions: assessmentDimensions,
    ref: assessmentsRef,
    refresh: assessmentsDimensionsRefresh,
  } = useElementDimensions()
  const {
    dimensions: therapyRecommendedDimensions,
    ref: therapyRecommendedRef,
    refresh: therapyRecommendedDimensionsRefresh,
  } = useElementDimensions()
  const {
    dimensions: suggestedTherapiesDimensions,
    ref: suggestedTherapiesRef,
    refresh: suggestedTherapiesDimensionsRefresh,
  } = useElementDimensions()
  const {
    dimensions: homunculusDimensions,
    ref: homunculusRef,
    refresh: homunculusDimensionsRefresh,
  } = useElementDimensions()
  const {
    dimensions: instructionsDimensions,
    ref: instructionsRef,
    refresh: instructionsDimensionsRefresh,
  } = useElementDimensions()
  const {
    dimensions: nextVisitDimensions,
    ref: nextVisitRef,
    refresh: nextVisitDimensionsRefresh,
  } = useElementDimensions()
  const {
    dimensions: signatureDimensions,
    ref: signatureRef,
    refresh: signatureDimensionsRefresh,
  } = useElementDimensions()

  const {
    dimensions: continuedFromDimensions,
    ref: continuedFromRef,
    refresh: continuedFromDimensionsRefresh,
  } = useElementDimensions()
  const {
    dimensions: patientHistoryNotesDimensions,
    ref: patientHistoryNotesRef,
    refresh: patientHistoryNotesDimensionsRefresh,
  } = useElementDimensions()
  
  // Additional effect to refresh patient history notes dimensions after content loads
  // Rich text content can take time to render and parse
  useEffect(() => {
    if (patientHistoryNotes && patientHistoryNotes.length > 0) {
      // Delay to ensure rich text HTML is fully rendered before measuring
      const timer = window.setTimeout(() => {
        patientHistoryNotesDimensionsRefresh()
      }, 200)
      
      return () => window.clearTimeout(timer)
    }
  }, [patientHistoryNotes, patientHistoryNotesDimensionsRefresh])

  const {
    dimensions: freeFormRichTextDimensions,
    ref: freeFormRichTextRef,
    refresh: freeFormRichTextDimensionsRefresh,
  } = useElementDimensions()

  useEffect(() => {
    if (freeFormRichText && String(freeFormRichText).trim().length > 0) {
      const timer = window.setTimeout(() => {
        freeFormRichTextDimensionsRefresh()
      }, 200)
      return () => window.clearTimeout(timer)
    }
  }, [freeFormRichText, freeFormRichTextDimensionsRefresh])

  const {
    dimensions: referredToDoctorNameDimensions,
    ref: referredToDoctorNameRef,
    refresh: referredToDoctorNameRefresh,
  } = useElementDimensions()

  const [debugHighlightStates, _setDebugHighlightStates] = useState({
    patientDetails: false,
    observations: false,
    complaints: false,
    diagnosis: false,
    drugAdvice: false,
    prescriptionAdvice: false,
    therapyRecommended: false,
    suggestedTherapies: false,
    homunculus: false,
    instructions: false,
    investigations: false,
    nextVisit: false,
  })

  const headerStyleToShow =
    printSettings?.printType === 'pdf'
      ? printSettings?.pdfHeaderType
      : printSettings?.printHeaderType

  const headerHeight = convertInchesToPixels(
    printSettings?.printType === 'pdf'
      ? printSettings?.pdfHeaderHeight
      : printSettings?.headerHeight,
    dpi,
  )

  const footerStyleToShow =
    printSettings?.printType === 'pdf'
      ? printSettings?.pdfFooterType
      : printSettings?.printFooterType

  const footerHeight = convertInchesToPixels(
    printSettings?.printType === 'pdf'
      ? printSettings?.pdfFooterHeight
      : printSettings?.footerHeight,
    dpi,
  )

  const refreshAllDimensions = useCallback(() => {
    a4PageDimensionsRefresh()
    headerDimensionsRefresh()
    footerDimensionsRefresh()
    patientDetailsDimensionsRefresh()
    observationsDimenstionsRefresh()
    complaintsDimensionsRefresh()
    diagnosisDimensionsRefresh()
    drugAdviceDimenstionsRefresh()
    prescriptionAdviceDimensionsRefresh()
    investigationsDimensionsRefresh()
    therapyRecommendedDimensionsRefresh()
    assessmentsDimensionsRefresh()
    suggestedTherapiesDimensionsRefresh()
    homunculusDimensionsRefresh()
    instructionsDimensionsRefresh()
    nextVisitDimensionsRefresh()
    signatureDimensionsRefresh()
    continuedFromDimensionsRefresh()
    refreshVitalDimensions()
    referredToDoctorNameRefresh()
    patientHistoryNotesDimensionsRefresh()
  }, [
    a4PageDimensionsRefresh,
    headerDimensionsRefresh,
    footerDimensionsRefresh,
    patientDetailsDimensionsRefresh,
    observationsDimenstionsRefresh,
    complaintsDimensionsRefresh,
    diagnosisDimensionsRefresh,
    drugAdviceDimenstionsRefresh,
    prescriptionAdviceDimensionsRefresh,
    investigationsDimensionsRefresh,
    therapyRecommendedDimensionsRefresh,
    assessmentsDimensionsRefresh,
    suggestedTherapiesDimensionsRefresh,
    homunculusDimensionsRefresh,
    instructionsDimensionsRefresh,
    nextVisitDimensionsRefresh,
    signatureDimensionsRefresh,
    continuedFromDimensionsRefresh,
    refreshVitalDimensions,
    referredToDoctorNameRefresh,
    patientHistoryNotesDimensionsRefresh,
  ])

  const leftSidebarWidth = convertInchesToPixels(printSettings?.leftSidebarWidth, dpi)
  const rightSidebarWidth = convertInchesToPixels(printSettings?.rightSidebarWidth, dpi)

  //log all the prescription items dimensions in map form

  const _dimensionsMap = {
    headerDimensions,
    footerDimensions,
    patientDetailsDimensions,
    observationDimensions,
    complaintsDimensions,
    diagnosisDimensions,
    drugAdviceTableDimensions,
    prescriptionAdviceDimensions,
    investigationsDimensions,
    therapyRecommendedDimensions,
    suggestedTherapiesDimensions,
    homunculusDimensions,
    instructionsDimensions,
    nextVisitDimensions,
    signatureDimensions,
    vitalsDimensions,
    patientHistoryNotesDimensions,
  }

  const orderOfPrescriptionItems = [
    // { orderId: 'patientDetails', title: 'Patient Details', dimension: patientDetailsDimensions },
    { orderId: 'patient-vitals', title: 'Patient Vitals', dimension: vitalsDimensions },
    { orderId: 'observations', title: 'Heath Observations', dimension: observationDimensions },
    { orderId: 'health_complaints', title: 'Patient Complaints', dimension: complaintsDimensions },
    { orderId: 'diagnosis', title: 'Diagnosis', dimension: diagnosisDimensions },
    { orderId: 'drug_advice', title: 'Rx', dimension: drugAdviceTableDimensions },
    {
      orderId: 'advice',
      title: 'Prescription Advice',
      dimension: prescriptionAdviceDimensions,
    },
    {
      orderId: 'investigations',
      title: 'Recommended Investigations',
      dimension: investigationsDimensions,
    },
    {
      orderId: PRESCRIPTION_ITEM_ORDER_IDS.assessments,
      title: 'Assessments',
      dimension: assessmentDimensions,
    },
    {
      orderId: 'therapyRecommended',
      title: 'Therapy Recommendations',
      dimension: therapyRecommendedDimensions,
    },

    {
      orderId: 'suggestedTherapies',
      title: 'Suggested Therapies',
      dimension: suggestedTherapiesDimensions,
    },

    {
      orderId: 'joint_homunculus',
      title: 'Body Diagram (Homunculus)',
      dimension: homunculusDimensions,
    },
    {
      orderId: 'instructions',
      title: 'Instructions for Patient',
      dimension: instructionsDimensions,
    },
    {
      orderId: PRESCRIPTION_ITEM_ORDER_IDS.refer_to,
      title: 'Referred To Doctor Name',
      dimension: referredToDoctorNameDimensions,
    },
    // Position free_form_rich_text relative to next_visit_details and signature based on
    // the configured emrSections order so the print layout matches the EMR section config.
    ...(() => {
      const freeFormItem = (
        checkShowInPrintForEmrSection(EMR_SECTION_IDS.FREE_FORM_RICH_TEXT, emrSections) &&
        freeFormRichText &&
        String(freeFormRichText).trim().length > 0
      ) ? [{ orderId: EMR_SECTION_IDS.FREE_FORM_RICH_TEXT, title: 'Free-form Rich Text', dimension: freeFormRichTextDimensions }] : []

      const nextVisitItem = [{ orderId: 'next_visit_details', title: 'Next Visit Schedule', dimension: nextVisitDimensions }]

      const signatureItem = checkShowInPrintForEmrSection(PRESCRIPTION_ITEM_ORDER_IDS.signature, emrSections)
        ? [{ orderId: PRESCRIPTION_ITEM_ORDER_IDS.signature, title: 'Signature', dimension: signatureDimensions?.height != null ? signatureDimensions : { height: 70, width: 200 } }]
        : []

      // All three are order-configurable via emrSections
      const orderableItems = [...freeFormItem, ...nextVisitItem, ...signatureItem]
      const getEmrIdx = (id) => {
        const idx = emrSections?.findIndex(s => s?.id === id)
        return idx != null && idx !== -1 ? idx : 9999
      }
      orderableItems.sort((a, b) => getEmrIdx(a.orderId) - getEmrIdx(b.orderId))
      return orderableItems
    })(),
    ...(footerStyleToShow === FOOTER_TYPES.onLastPage ||
    footerStyleToShow === FOOTER_TYPES.inLineAtEnd
      ? [
          {
            orderId: 'prescriptionFooter',
            title: 'Footer',
            dimension: footerDimensions,
          },
        ]
      : []),
    // Note: Patient History Notes are rendered separately after all pages
    // to allow free-flowing content across multiple pages
  ]

  // Extract drug advice table type for format-aware chunking
  const drugAdviceTableType = prescriptionViewSettings?.drugAdviceTablePrintType 
    || prescriptionViewSettings?.paperPrintSettings?.drugAdviceTablePrintType
    || 'default'

  const itemRenderingDetailsList = useMemo(
    () =>
      getPrescriptionItemRenderingList(
        orderOfPrescriptionItems,
        headerHeight + patientDetailsDimensions?.height,
        footerHeight || 0,
        printSettings?.leftSidebarWidth,
        printSettings?.rightSidebarWidth,
        a4PageDimensions?.height,
        headerStyleToShow,
        footerStyleToShow,
        null,
        continuedFromDimensions?.height || 0,
        drugsAdvice,
        patientHistoryNotes,
        useBestFit,
        dpi,
        colorMode,
        drugAdviceTableType,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      vitalsDimensions, observationDimensions, complaintsDimensions,
      diagnosisDimensions, drugAdviceTableDimensions, prescriptionAdviceDimensions,
      investigationsDimensions, assessmentDimensions, therapyRecommendedDimensions,
      suggestedTherapiesDimensions, homunculusDimensions, instructionsDimensions,
      referredToDoctorNameDimensions, nextVisitDimensions, signatureDimensions,
      footerDimensions, patientDetailsDimensions, continuedFromDimensions,
      a4PageDimensions, patientHistoryNotesDimensions, freeFormRichTextDimensions,
      headerHeight, footerHeight, headerStyleToShow, footerStyleToShow,
      printSettings,
      drugsAdvice, patientHistoryNotes, freeFormRichText,
      useBestFit, dpi, colorMode, drugAdviceTableType,
      emrSections,
    ],
  )
  // Notify parent component of total pages
  useEffect(() => {
    if (onTotalPagesChange && itemRenderingDetailsList?.length) {
      onTotalPagesChange(itemRenderingDetailsList.length)
    }
  }, [itemRenderingDetailsList?.length, onTotalPagesChange])

  // Use ref to track if we're currently refreshing to prevent infinite loops
  const isRefreshingRef = useRef(false)
  
  useEffect(() => {
    // Prevent infinite loops by checking if we're already refreshing
    if (isRefreshingRef.current) {
      return
    }
    
    isRefreshingRef.current = true
    // Use requestAnimationFrame to batch the updates and prevent immediate re-renders
    window.requestAnimationFrame(() => {
      refreshAllDimensions()
      // Reset the flag after a short delay to allow state updates to complete
      window.setTimeout(() => {
        isRefreshingRef.current = false
      }, 100)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, refreshDimensionsDependencies)

  // Show uploaded images in print view
  // If prescription_images EMR section exists, respect its printable setting
  // If section doesn't exist (backward compatibility), always show images if they exist
  const uploadedImagesFromContext = prescriptionData?.prescriptionData?.uploadedImages
  const _uploadedSignedImagesFromContext = prescriptionData?.prescriptionData?.uploadedSignedImages
  
  const hasUploadedImages = 
    !!uploadedImagesFromContext &&
    uploadedImagesFromContext?.length > 0
  
  // Only show uploaded images when the prescription_images EMR section explicitly exists
  // and is configured as printable. If the section is absent (disabled/not configured),
  // images are NOT printed — this matches what the user sees in the editor (section off = not printed).
  const shouldShowUploadedImages = hasUploadedImages &&
    checkAndRenderBasedOnEmrSections('prescription_images', emrSections)

  // NOTE: Early returns moved to after all hooks are defined (see after renderContinuousView)
  // to avoid "Rendered fewer hooks than expected" error





  // Early returns (moved here after all hooks are defined to avoid
  // "Rendered fewer hooks than expected" error)
  
  // Old UI early return
  if (useOldUI) {
    return (
      <OldPrescriptionViewComponent
        debugMode={debugMode}
        drugsAdvice={drugsAdvice}
        honorPropValue={honorPropValue}
        jointHomunculusData={jointHomunculusData}
        nextVisitInputType={nextVisitInputType}
        nextVisitInputValue={nextVisitInputValue}
        placeholder={placeholder}
        prescriptionData={prescriptionData}
        prescriptionViewSettings={prescriptionViewSettings}
        printSettings={printSettings}
        printType={printType}
        selectedAdvice={selectedAdvice}
        selectedComplaints={selectedComplaints}
        selectedDiagnosis={selectedDiagnosis}
        selectedInstructions={selectedInstructions}
        selectedInvestigations={selectedInvestigations}
        selectedObservations={selectedObservations}
        selectedAssessments={selectedAssessments}
        selectedTherapies={selectedTherapies}
        oldUIDrugSubSections={oldUIDrugSubSections}
        setPropValue={setPropValue}
        setValue={setValue}
        suggestedBookingTherapy={suggestedBookingTherapy}
        finishTestsRequestedBy={finalRequestedTestsBy}
        referredToDoctorName={referredToDoctorName}
        vital={vital}
        patientHistoryNotes={patientHistoryNotes}
        emrSections={emrSections}
        drugEmrSubSections={
          oldUIDrugSubSections
            ? oldUIDrugSubSections
            : emrSections?.find((item) => item?.id === 'drug_advice')?.subSections
        }
        value={value}
        useFullUrlForHeaderImages={useFullUrlForHeaderImages}
        useFullUrlForFooterImages={useFullUrlForFooterImages}
      />
    )
  }

  // NOTE: Removed early return for uploaded images. 
  // Images are now shown AFTER the prescription content, not INSTEAD of it.

  return (
    <A4WrapperContainer showInUI={showInUI}>
      <style>
        {`
          /* Responsive styles for all viewport sizes */
          @media (max-width: 1200px) {
            /* Scale prescription content when viewport is smaller than typical A4 width */
            .prescription-page,
            .preview-page-container {
              max-width: 100% !important;
              width: 100% !important;
              box-sizing: border-box !important;
            }
            
            /* Ensure A4Page components scale */
            [class*="A4Page"],
            [class*="A4PreviewWrapper"] {
              max-width: 100% !important;
              width: 100% !important;
            }
            
            /* Scale PMPConsultationParent to fit - override inline maxWidth */
            [class*="PMPConsultationParent"] {
              max-width: 100% !important;
              width: 100% !important;
            }
            
            /* Ensure A4PageMeasureComponentWrapperContainer scales */
            [class*="A4PageMeasureComponentWrapperContainer"] {
              max-width: 100% !important;
              width: 100% !important;
            }
          }
          
          /* Additional responsive styles for smaller viewports */
          @media (max-width: 900px) {
            /* Force all prescription page containers to scale */
            .prescription-page > div,
            .prescription-page > [class*="A4Page"],
            .prescription-page [class*="A4PageContainer"],
            .prescription-page [class*="A4PreviewWrapper"],
            .prescription-page [class*="A4PageMeasureComponentWrapperContainer"],
            .prescription-page [class*="PMPConsultationParent"] {
              max-width: 100% !important;
              width: 100% !important;
            }
          }
          
          /* Mobile responsive styles */
          @media (max-width: 768px) {
            /* Scale prescription content to fit mobile */
            .prescription-page,
            [class*="A4Page"],
            [class*="prescription"] {
              max-width: 100% !important;
            }
            
            /* Ensure tables and content fit within mobile viewport */
            table {
              width: 100% !important;
              max-width: 100% !important;
              table-layout: auto !important;
            }
            
            /* Allow text to wrap */
            td, th, p, div {
              word-wrap: break-word !important;
              word-break: break-word !important;
            }
            
            /* Scale images */
            img {
              max-width: 100% !important;
              height: auto !important;
            }
          }
          
          @media print {
            /* Hide all UI elements, buttons, and debug labels */
            /* These rules apply unconditionally to ensure UI is hidden */
            .hide-on-print,
            .hide-on-print-only,
            button,
            [class*="MuiButton"],
            [class*="MuiIconButton"],
            [class*="MuiToggleButton"],
            [class*="MuiToggleButtonGroup"],
            [class*="MuiBackdrop"],
            [class*="MuiDrawer"]:not([class*="print"]),
            [class*="MuiDialog"],
            [class*="MuiModal"],
            input[type="button"],
            input[type="submit"],
            a[role="button"],
            /* Hide view toggle and dimension controls */
            [class*="MuiBox-root"]:has([class*="MuiToggleButtonGroup"]),
            [class*="MuiBox-root"]:has(button[class*="MuiButton"]),
            /* Hide debug labels and floating elements */
            div[style*="position: absolute"][style*="right"]:not([class*="print"]),
            div[style*="position:absolute"][style*="right"]:not([class*="print"]),
            div[style*="position: fixed"]:not([class*="print"]),
            div[style*="position:fixed"]:not([class*="print"]),
            /* Hide elements with visibility hidden */
            [style*="visibility: hidden"],
            [style*="visibility:hidden"] {
              display: none !important;
              visibility: hidden !important;
              opacity: 0 !important;
              height: 0 !important;
              width: 0 !important;
              overflow: hidden !important;
            }
            /* Additional specific rules for view toggle and UI controls */
            /* Hide any Box containing toggle buttons - use higher specificity */
            body [class*="MuiBox-root"].hide-on-print,
            html body [class*="MuiBox-root"].hide-on-print,
            body .hide-on-print,
            html body .hide-on-print {
              display: none !important;
              visibility: hidden !important;
              opacity: 0 !important;
              height: 0 !important;
              width: 0 !important;
              overflow: hidden !important;
            }
            /* Hide ToggleButtonGroup and related controls */
            body [class*="MuiToggleButtonGroup"],
            html body [class*="MuiToggleButtonGroup"],
            body [class*="MuiToggleButton"],
            html body [class*="MuiToggleButton"] {
              display: none !important;
              visibility: hidden !important;
            }
            /* Hide sticky positioned elements that are UI controls */
            body [style*="position: sticky"]:not([class*="print"]),
            body [style*="position:sticky"]:not([class*="print"]),
            html body [style*="position: sticky"]:not([class*="print"]),
            html body [style*="position:sticky"]:not([class*="print"]) {
              display: none !important;
              visibility: hidden !important;
            }
          }
        `}
      </style>
      <Box
        sx={{
          paddingTop: '0',
          position: 'relative',
          zIndex: 0,
          width: '100%',
          minWidth: 0,
          overflowX: 'auto',
          overflowY: 'visible',
          '@media print': {
            position: 'static !important',
            display: 'block !important',
            width: '100% !important',
            maxWidth: 'none !important',
            height: 'auto !important',
            minHeight: 'auto !important',
            maxHeight: 'none !important',
            overflow: 'visible !important',
            padding: '0 !important',
            margin: '0 !important',
            transform: 'none !important',
            zoom: '1 !important',
            contain: 'none !important',
          },
        }}
      >
        {/* Page break view */}
        {itemRenderingDetailsList?.map((pageDetails, breakIdx, allPagesData) => {
        const _isLastPage = allPagesData.length - 1 === breakIdx
        return (
          <React.Fragment key={breakIdx + 1}>
            <div 
              className="prescription-page preview-page-container"
              data-page-number={breakIdx + 1}
              style={{ 
                pageBreakAfter: breakIdx < allPagesData.length - 1 ? 'always' : 'auto',
                pageBreakInside: 'avoid',
                // Remove position: relative to allow normal document flow in print
                // position: 'relative', // Will be overridden by print CSS to 'static'
                width: '100%',
                margin: 0,
                padding: 0,
                display: 'block'
              }}
            >
            <A4PageComponent
              leftMarginOffsetInPx={leftMarginOffsetInPx}
              rightMarginOffsetInPx={rightMarginOffsetInPx}
              topMarginOffsetInPx={topMarginOffsetInPx}
              bottomMarginOffsetInPx={bottomMarginOffsetInPx}
              showTheA4Page={showTheA4Page}
              a4WidthInPx={(widthOfA4Page || 0) - 0.5}
              dpi={dpi}
              a4HeightInPx={heightOfA4Page}
              footerDimensions={footerDimensions}
              headerDimensions={headerDimensions}
              footerStyleToShow={footerStyleToShow}
              headerStyleToShow={headerStyleToShow}
              prescriptionData={prescriptionData}
              pageIdx={breakIdx}
              isLastPage={allPagesData.length - 1 === breakIdx}
            >
                <A4PreviewWrapper
                margin={0}
                // pageStartTop={breakHeight + 20}
                pageNumber={breakIdx + 1}
                a4Width={widthOfA4Page}
                showPageView
                removeThePaddingAndMargins={showTheA4Page}
                heightOfOnePageInPixels={heightOfPrescription}
                // debugMode={debugMode}
                zIndex={0}
              >
                <Box sx={{ position: 'relative', display: 'block', width: '100%' }}>
                  <PMPConsultationParent
                    ref={(el) => {
                      if (el) {
                        rectangleRefs.current[`page-${breakIdx + 1}`] = el
                      }
                    }}
                    biggerFont={prescriptionData?.layout === PRESCRIPTION_LAYOUTS.largerFontLayout}
                    noBorder={printType === PRESCRIPTION_PRINT_TYPES.withHeaderAndFooterBlankSpace}
                    className={
                      printType === PRESCRIPTION_PRINT_TYPES.withHeaderAndFooterBlankSpace
                        ? ''
                        : 'final-printable make-all-borders-black-print'
                    }
                    hiddenInUI={
                      prescriptionViewSettings?.prescriptionBuilderViewType ===
                      PRESCRIPTION_BUILDER_VIEW_TYPES.tabletView
                    }
                    style={{
                      width: '100%',
                      maxWidth: `${widthOfA4Page}px`,
                      // For pages with patient history notes, allow height: auto so CSS page-break handles
                      // multi-page overflow on print. Free-form rich text is now properly chunked (windowed
                      // rendering) so it always fits within the prescribed height — no special case needed.
                      height: pageDetails?.items?.includes(PRESCRIPTION_ITEM_ORDER_IDS.patientHistoryNotes)
                        ? 'auto' 
                        : `${heightOfPrescription}px`,
                      minHeight: pageDetails?.items?.includes(PRESCRIPTION_ITEM_ORDER_IDS.patientHistoryNotes)
                        ? 'auto'
                        : `${heightOfPrescription}px`,
                      '@media print': {
                        height: 'auto',
                        minHeight: 'auto',
                      }
                    }}
                  >
                  <PrescriptionPrintableHeader
                    currentPage={breakIdx + 1}
                    debugMode={debugMode}
                    hideDebugLabels
                    headerDimensionsHeight={headerDimensions?.height}
                    headerHeight={
                      // When leaveSpace is true, always use the headerHeight from printSettings
                      // Otherwise, use headerHeight only if pageDetails?.header is true
                      printSettings?.blankHeader ? headerHeight : (pageDetails?.header ? headerHeight : 0)
                    }
                    headerStyleToShow={headerStyleToShow}
                    idx={breakIdx}
                    pageHeight={heightOfPrescription}
                    prescriptionData={prescriptionData}
                    printType={printType}
                    showBottomBorder={breakIdx !== 0}
                    leaveSpace={printSettings?.blankHeader}
                    useFullUrlForHeaderImages={useFullUrlForHeaderImages}
                  />
                  {pageDetails?.header && (
                    <div className="d-flex justify-content-center" data-print-patient-info="true">
                      {pageDetails?.leftSidebar && (
                        <div style={{ width: `${leftSidebarWidth}px`, flexShrink: 0 }}></div>
                      )}
                      <div style={{ flex: 1, maxWidth: '100%' }}>
                        <PatientDetails
                          // debug={debug}
                          prescriptionData={prescriptionData}
                          printType={printType}
                          debugHighlightStates={debugHighlightStates}
                          prescriptionViewSettings={prescriptionViewSettings}
                        />
                      </div>
                      {pageDetails?.rightSidebar && (
                        <div style={{ width: `${rightSidebarWidth}px`, flexShrink: 0 }}></div>
                      )}
                    </div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, width: '100%' }}>
                    <PrescriptionPrintableBody
                      value={value}
                      setValue={setValue}
                      placeholder={placeholder}
                      alsoChangePropValue={Boolean(honorPropValue)}
                      setPropValue={setPropValue}
                      selectedAdvice={selectedAdvice}
                      selectedTherapies={selectedTherapies}
                      nextVisitInputType={nextVisitInputType}
                      nextVisitInputValue={nextVisitInputValue}
                      prescriptionData={prescriptionData}
                      suggestedBookingTherapy={suggestedBookingTherapy}
                      styleOverrides={enhancedStyleOverrides}
                      colorMode={colorMode}
                      getSectionColor={getSectionColor}
                      selectedAssessments={selectedAssessments}
                      selectedInstructions={selectedInstructions}
                      selectedInvestigations={selectedInvestigations} // todo: put in ViewOnlyPrescriptions - both type
                      referredToDoctorName={referredToDoctorName}
                      finalRequestedTestsBy={finalRequestedTestsBy}
                      jointHomunculusData={jointHomunculusData}
                      prescriptionViewSettings={prescriptionViewSettings}
                      debug={debugMode}
                      drugsAdvice={drugsAdvice}
                      emrSections={emrSections}
                      selectedObservations={selectedObservations}
                      selectedComplaints={selectedComplaints}
                      selectedDiagnosis={selectedDiagnosis}
                      vital={vital}
                      printType={printType}
                      A4Height={heightOfPrescription}
                      practitionerId={practitionerId}
                      footerHeight={footerDimensions?.height}
                      headerHeight={headerDimensions?.height}
                      debugHighlightStates={debugHighlightStates}
                      currentPage={breakIdx + 1}
                      pageDetailsObject={pageDetails}
                      leftSidebar={pageDetails?.leftSidebar}
                      rightSidebar={pageDetails?.rightSidebar}
                      leftSidebarWidth={leftSidebarWidth}
                      rightSidebarWidth={rightSidebarWidth}
                      totalPages={itemRenderingDetailsList?.length}
                      footerType={footerStyleToShow}
                      patientHistoryNotes={patientHistoryNotes}
                      patientHistoryNotesRef={patientHistoryNotesRef}
                      signatureRef={signatureRef}
                      isLastPage={itemRenderingDetailsList?.length - 1 === breakIdx}
                      useEmptyBox={useEmptyBox}
                      useFullUrlForFooterImages={useFullUrlForFooterImages}
                      allPagesData={itemRenderingDetailsList}
                      freeFormRichText={freeFormRichText}
                      freeFormSection={emrSections?.find((s) => s?.id === EMR_SECTION_IDS.FREE_FORM_RICH_TEXT)}
                    />
                  </div>
                  {/* Only render separate footer if footer is not inline (handled inside PrescriptionPrintableBody) */}
                  {footerStyleToShow !== FOOTER_TYPES.inLineAtEnd && pageDetails?.footer && (
                    <PrescriptionPrintableFooter
                      debugMode={debugMode}
                      prescriptionData={prescriptionData}
                      footerStyleToShow={footerStyleToShow}
                      footerHeight={footerHeight}
                      printType={printType}
                      leaveSpace={printSettings?.blankFooter}
                      styleOverrides={
                        prescriptionViewSettings?.paperPrintSettings?.footerSettings?.printStyles
                      }
                      useFullUrlForFooterImages={useFullUrlForFooterImages}
                    />
                  )}
                </PMPConsultationParent>
                </Box>
              </A4PreviewWrapper>
            </A4PageComponent>
            </div>
          </React.Fragment>
        )
      })}
      </Box>

      {/* Uploaded Images Section - Shown AFTER prescription content */}
      <PrescriptionSectionErrorBoundary sectionName="Uploaded Images">
      {shouldShowUploadedImages && (() => {
        const uploadedImages = prescriptionData?.prescriptionData?.uploadedImages || []
        const basePageNumber = (itemRenderingDetailsList?.length || 0) + 1
        
        return uploadedImages.map((imageMediaId, imageIndex) => (
          <Box
            key={`prescription-image-page-${imageMediaId}-${imageIndex}`}
            component="div"
            className="prescription-page preview-page-container"
            data-page-number={basePageNumber + imageIndex}
            sx={{ 
              mt: imageIndex === 0 ? 2 : 0,
              width: '100%',
              margin: 0,
              padding: 0,
              display: 'block',
              '@media print': {
                pageBreakBefore: imageIndex === 0 ? 'always' : 'always',
                pageBreakAfter: imageIndex < uploadedImages.length - 1 ? 'always' : 'auto',
                pageBreakInside: 'avoid',
                marginTop: 0,
              }
            }}
          >
            <Box
              sx={{
                width: '100%',
                maxWidth: '794px',
                margin: '0 auto',
                '@media print': {
                  maxWidth: '100%',
                }
              }}
            >
              <ImageWithMenu
                imageMediaId={imageMediaId}
                index={imageIndex}
                menuItems={[]}
                alt="uploaded prescription"
              />
            </Box>
          </Box>
        ))
      })()}
      </PrescriptionSectionErrorBoundary>

      {/* Patient History Notes - Rendered after all prescription pages and images */}
      {/* Create multiple A4 page containers for content to flow across */}
      <PrescriptionSectionErrorBoundary sectionName="Patient History Notes">
      {(() => {
        const patientHistorySection = emrSections?.find((s) => s?.id === 'patient-history-notes')
        const isPrintableOnRx = patientHistorySection?.printable || false
        if (!isPrintableOnRx || !Array.isArray(patientHistoryNotes) || patientHistoryNotes?.length === 0) {
          return null
        }

        const prescriptionPagesCount = itemRenderingDetailsList?.length || 0
        const imagesCount = prescriptionData?.prescriptionData?.uploadedImages?.length || 0
        const startingPageNumber = prescriptionPagesCount + imagesCount + 1

        // Estimate pages needed based on content
        // Rough estimate: each note might take 200-400px, heading takes ~100px
        const estimatedContentHeight = patientHistoryNotes.reduce((total, note) => {
          const contentLength = (note?.content?.content || '').length
          // Estimate: ~150px base + ~0.4px per character
          return total + 150 + (contentLength * 0.4)
        }, 100) // 100px for heading
        
        const estimatedPages = Math.max(1, Math.ceil(estimatedContentHeight / heightOfPrescription))
        // Create enough pages to accommodate content (cap at 20 pages)
        const _pagesToCreate = Math.min(Math.max(estimatedPages, 2), 20)

        return (
          <>
            <style>{`
              .patient-history-notes-wrapper {
                width: 100%;
              }
              .patient-history-notes-page {
                width: 100%;
                margin: 0;
                padding: 0;
                display: block;
              }
              @media print {
                .patient-history-notes-page {
                  page-break-after: auto !important;
                  break-after: auto !important;
                  page-break-inside: auto !important;
                  break-inside: auto !important;
                }
                .patient-history-notes-container .A4PageMeasureComponentWrapperContainer {
                  page-break-inside: auto !important;
                  break-inside: auto !important;
                  height: auto !important;
                  min-height: auto !important;
                  max-height: none !important;
                }
                .patient-history-notes-container .A4PageContainer {
                  page-break-inside: auto !important;
                  break-inside: auto !important;
                  page-break-after: auto !important;
                  break-after: auto !important;
                  height: auto !important;
                  min-height: auto !important;
                }
                .patient-history-notes-content-wrapper {
                  page-break-inside: auto !important;
                  break-inside: auto !important;
                  height: auto !important;
                  min-height: auto !important;
                  max-height: none !important;
                }
                /* Ensure all nested elements allow page breaks */
                .patient-history-notes-container * {
                  page-break-inside: auto;
                  break-inside: auto;
                }
                /* Try to keep individual notes together, but allow breaking if too long */
                .patient-history-notes-container .patient-history-note-item {
                  page-break-inside: avoid;
                  break-inside: avoid;
                }
                /* Allow breaking within note content if note is very long */
                .patient-history-notes-container .patient-history-note-item .tiptap-content {
                  page-break-inside: auto;
                  break-inside: auto;
                }
              }
            `}</style>
            {/* Single container with content - CSS will handle page breaks in print */}
            <div 
              className="prescription-page preview-page-container patient-history-notes-page patient-history-notes-container"
              data-page-number={startingPageNumber}
              style={{ 
                pageBreakBefore: 'always',
                width: '100%',
                margin: 0,
                padding: 0,
                display: 'block',
              }}
            >
              <A4PageComponent
                leftMarginOffsetInPx={leftMarginOffsetInPx}
                rightMarginOffsetInPx={rightMarginOffsetInPx}
                topMarginOffsetInPx={topMarginOffsetInPx}
                bottomMarginOffsetInPx={bottomMarginOffsetInPx}
                showTheA4Page={showTheA4Page}
                a4WidthInPx={(widthOfA4Page || 0) - 0.5}
                dpi={dpi}
                a4HeightInPx={heightOfA4Page}
                footerDimensions={null}
                headerDimensions={null}
                footerStyleToShow={null}
                headerStyleToShow={null}
                prescriptionData={prescriptionData}
                pageIdx={startingPageNumber - 1}
                isLastPage={false}
              >
                <A4PreviewWrapper
                  margin={0}
                  pageNumber={startingPageNumber}
                  a4Width={widthOfA4Page}
                  showPageView
                  removeThePaddingAndMargins={showTheA4Page}
                  heightOfOnePageInPixels={0}
                  zIndex={0}
                >
                  <Box sx={{ position: 'relative', display: 'block', width: '100%' }}>
                    <PMPConsultationParent
                      biggerFont={prescriptionData?.layout === PRESCRIPTION_LAYOUTS.largerFontLayout}
                      noBorder={printType === PRESCRIPTION_PRINT_TYPES.withHeaderAndFooterBlankSpace}
                      className={
                        printType === PRESCRIPTION_PRINT_TYPES.withHeaderAndFooterBlankSpace
                          ? ''
                          : 'final-printable make-all-borders-black-print patient-history-notes-container'
                      }
                      style={{ width: '100%', maxWidth: `${widthOfA4Page}px`, height: 'auto', minHeight: 'auto' }}
                    >
                      <PrescriptionPrintableHeader
                        currentPage={startingPageNumber}
                        debugMode={debugMode}
                        hideDebugLabels
                        headerDimensionsHeight={headerDimensions?.height}
                        headerHeight={headerHeight}
                        headerStyleToShow={headerStyleToShow}
                        printType={printType}
                        prescriptionData={prescriptionData}
                        useFullUrlForHeaderImages={useFullUrlForHeaderImages}
                      />
                      <Box
                        className="patient-history-notes-content-wrapper"
                        sx={{
                          padding: '20px',
                          height: 'auto',
                          minHeight: 'auto',
                          overflow: 'visible',
                          boxSizing: 'border-box',
                          flex: 1,
                          '@media print': {
                            padding: '16px',
                            height: 'auto !important',
                            minHeight: 'auto !important',
                            maxHeight: 'none !important',
                            pageBreakInside: 'auto !important',
                            breakInside: 'auto !important',
                            overflow: 'visible !important',
                          }
                        }}
                      >
                        <PrintablePatientHistoryNotes
                          patientHistoryNotes={patientHistoryNotes}
                          patientHistoryNotesRef={patientHistoryNotesRef}
                          prescriptionViewSettings={prescriptionViewSettings}
                          debug={debugMode}
                          debugHighlightStates={debugHighlightStates}
                          styleOverrides={styleOverrides?.[EMR_SECTION_IDS.PATIENT_HISTORY_NOTES]}
                        />
                      </Box>
                      {footerStyleToShow !== FOOTER_TYPES.inLineAtEnd && (
                        <PrescriptionPrintableFooter
                          debugMode={debugMode}
                          prescriptionData={prescriptionData}
                          footerStyleToShow={footerStyleToShow}
                          footerHeight={footerHeight}
                          printType={printType}
                          leaveSpace={printSettings?.blankFooter}
                          styleOverrides={
                            prescriptionViewSettings?.paperPrintSettings?.footerSettings?.printStyles
                          }
                          useFullUrlForFooterImages={useFullUrlForFooterImages}
                        />
                      )}
                    </PMPConsultationParent>
                  </Box>
                </A4PreviewWrapper>
              </A4PageComponent>
            </div>
          </>
        )
      })()}
      </PrescriptionSectionErrorBoundary>

      {/* hidden prescription ui */}
      <div
        className="hide-on-print"
        style={{
          position: 'absolute',
          visibility: 'hidden',
          height: '0px',
          overflow: 'hidden',
          top: 0,
          width: `${prescriptionWidth}px`,
        }}
      >
        <A4PreviewWrapper
          key={0}
          a4Width={prescriptionWidth}
          margin={0}
          showPageView
          heightOfOnePageInPixels={heightOfPrescription}
          pageRef={a4PageRef}
        >
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                  <PMPConsultationParent
                    ref={(el) => {
                      if (el) {
                        rectangleRefs.current[`hidden-0`] = el
                      }
                    }}
                    biggerFont={prescriptionData?.layout === PRESCRIPTION_LAYOUTS.largerFontLayout}
                    noBorder={printType === PRESCRIPTION_PRINT_TYPES.withHeaderAndFooterBlankSpace}
                    className={
                      printType === PRESCRIPTION_PRINT_TYPES.withHeaderAndFooterBlankSpace
                        ? ''
                        : 'final-printable make-all-borders-black-print'
                    }
                    hiddenInUI={
                      prescriptionViewSettings?.prescriptionBuilderViewType ===
                      PRESCRIPTION_BUILDER_VIEW_TYPES.tabletView
                    }
                  >
            <div ref={headerRef}>
              <PrescriptionHeaderRender
                prescriptionData={prescriptionData}
                leaveSpace={false}
                debug={debugMode}
                printType={printType}
                headerHeight={headerHeight}
                useCompleteImageLink={useFullUrlForHeaderImages}
              />
            </div>
            <PatientDetails
              // debug={debug}
              prescriptionData={prescriptionData}
              printType={printType}
              debugHighlightStates={debugHighlightStates}
              prescriptionViewSettings={prescriptionViewSettings}
              containerRef={patientDetailsRef}
            />

            <ContinuedToAndFromDiv ref={continuedFromRef} currentText={`Continued from  Page 1`} />
            <div className="d-flex justify-content-center">
              <div style={{ width: `${leftSidebarWidth}px`, flexShrink: 0 }}></div>
              <PrescriptionFullBody
                value={value}
                setValue={setValue}
                placeholder={placeholder}
                alsoChangePropValue={Boolean(honorPropValue)}
                setPropValue={setPropValue}
                selectedAdvice={selectedAdvice}
                selectedTherapies={selectedTherapies}
                nextVisitInputType={nextVisitInputType}
                nextVisitInputValue={nextVisitInputValue}
                prescriptionData={prescriptionData}
                suggestedBookingTherapy={suggestedBookingTherapy}
                selectedInstructions={selectedInstructions}
                selectedInvestigations={selectedInvestigations} // todo: put in ViewOnlyPrescriptions - both type
                jointHomunculusData={jointHomunculusData}
                prescriptionViewSettings={prescriptionViewSettings}
                debug={debugMode}
                drugsAdvice={drugsAdvice}
                selectedObservations={selectedObservations}
                selectedComplaints={selectedComplaints}
                selectedDiagnosis={selectedDiagnosis}
                selectedAssessments={selectedAssessments}
                finalRequestedTestsBy={finalRequestedTestsBy}
                vital={vital}
                emrSections={emrSections}
                printType={printType}
                A4Height={heightOfPrescription}
                footerHeight={footerDimensions?.height}
                headerHeight={headerDimensions?.height}
                patientDetailsRef={patientDetailsRef}
                practitionerId={practitionerId}
                observationRef={observationRef}
                complaintRef={complaintsRef}
                diagnosisRef={diagnosisRef}
                drugAdviceRef={drugAdviceTableRef}
                prescriptionAdviceRef={prescriptionAdviceRef}
                complaintsRef={complaintsRef}
                drugAdviceTableRef={drugAdviceTableRef}
                homunculusRef={homunculusRef}
                instructionsRef={instructionsRef}
                investigationsRef={investigationsRef}
                referredToDoctorName={referredToDoctorName}
                referredToDoctorNameRef={referredToDoctorNameRef}
                therapyRecommendedRef={therapyRecommendedRef}
                assessmentsRef={assessmentsRef}
                nextVisitRef={nextVisitRef}
                signatureRef={signatureRef}
                suggestedTherapiesRef={suggestedTherapiesRef}
                prescriptionBodyContainerRef={prescriptionContinuousBodyRef}
                vitalsRef={vitalsRef}
                patientHistoryNotes={patientHistoryNotes}
                patientHistoryNotesRef={patientHistoryNotesRef}
                freeFormRichText={freeFormRichText}
                freeFormRichTextRef={freeFormRichTextRef}
                freeFormSection={emrSections?.find((s) => s?.id === EMR_SECTION_IDS.FREE_FORM_RICH_TEXT)}
                useFullUrlForFooterImages={useFullUrlForFooterImages}
                styleOverrides={styleOverrides}
              />
              <div style={{ width: `${rightSidebarWidth}px`, flexShrink: 0 }}></div>
            </div>
            <ContinuedToAndFromDiv currentText={`Continuing to Page 2`} />
            <PMPV2FooterContainer
              ref={footerRef}
              debug={debugMode}
              style={{ height: `${footerHeight}px` }}
            >
              <div>
                <PrescriptionFooter
                  prescriptionData={prescriptionData}
                  leaveSpace={false}
                  debug={debugMode}
                  footerRef={footerRef}
                  useCompleteImageLink={useFullUrlForFooterImages}
                />
              </div>
            </PMPV2FooterContainer>
          </PMPConsultationParent>
                </Box>
        </A4PreviewWrapper>
      </div>
    </A4WrapperContainer>
  )
}

function A4PageComponent({
  a4HeightInPx,
  a4WidthInPx,
  showTheA4Page = false,
  leftMarginOffsetInPx = 0,
  rightMarginOffsetInPx = 0,
  topMarginOffsetInPx = 0,
  bottomMarginOffsetInPx = 0,
  prescriptionData,
  headerDimensions,
  footerDimensions,
  headerStyleToShow,
  footerStyleToShow,
  dpi,
  pageIdx,
  isLastPage = false,
  children,
}) {
  if (showTheA4Page) {
    return (
      <>
        <A4PageBackgroundContainer
          leftMarginOffset={leftMarginOffsetInPx}
          rightMarginOffset={rightMarginOffsetInPx}
          topMarginOffset={topMarginOffsetInPx}
          bottomMarginOffset={bottomMarginOffsetInPx}
          width={a4WidthInPx}
          height={a4HeightInPx}
        >
          <div style={{ height: '0px', position: 'relative', width: '0px' }}>
            {/* Horizontal line with arrow and text */}
            <div
              style={{
                position: 'absolute',
                width: `${a4WidthInPx}px`,
                height: '1px',
                top: '-10px',
                background: 'black',
              }}
            >
              {/* Arrow tip at the end */}
              <div
                style={{
                  position: 'absolute',
                  right: '-5px',
                  top: '-4px',
                  width: 0,
                  height: 0,
                  borderLeft: '5px solid black',
                  borderTop: '4px solid transparent',
                  borderBottom: '4px solid transparent',
                }}
              ></div>
              {/* Text label on top */}
              <span
                style={{
                  position: 'absolute',
                  top: '-20px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  fontSize: '12px',
                  fontWeight: '500',
                  whiteSpace: 'nowrap',
                }}
              >
                Width: {A4_DIMENSIONS_IN_INCHES.width}in
              </span>
            </div>

            {/* Vertical line with arrow and text */}
            <div
              style={{
                position: 'absolute',
                height: `${a4HeightInPx}px`,
                width: '1px',
                left: '-10px',
                background: 'black',
              }}
            >
              {/* Arrow tip at the end */}
              <div
                style={{
                  position: 'absolute',
                  bottom: '-5px',
                  left: '-4px',
                  width: 0,
                  height: 0,
                  borderTop: '5px solid black',
                  borderLeft: '4px solid transparent',
                  borderRight: '4px solid transparent',
                }}
              ></div>
              {/* Text label on the left (rotated) */}
              <span
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '-10px',
                  transform: 'rotate(-90deg) translateX(-50%)',
                  transformOrigin: 'left center',
                  fontSize: '12px',
                  fontWeight: '500',
                  whiteSpace: 'nowrap',
                }}
              >
                Height: {A4_DIMENSIONS_IN_INCHES.height}in
              </span>
            </div>
            <PrescriptionHeaderBodyFooterDebugLabels
              a4PageWidth={a4WidthInPx}
              headerDimensions={headerDimensions}
              headerStyleToShow={headerStyleToShow}
              footerStyleToShow={footerStyleToShow}
              footerDimensions={footerDimensions}
              a4PageHeight={a4HeightInPx}
              prescriptionData={prescriptionData}
              dpi={dpi}
              marginOffsets={{
                marginLeft: leftMarginOffsetInPx,
                marginRight: rightMarginOffsetInPx,
                marginTop: topMarginOffsetInPx,
                marginBottom: bottomMarginOffsetInPx,
              }}
              pageIdx={pageIdx}
              isLastPage={isLastPage}
            />
          </div>
          <div
            className="print-margin-wrapper"
            style={{
              paddingLeft: `${leftMarginOffsetInPx}px`,
              paddingRight: `${rightMarginOffsetInPx}px`,
              paddingTop: `${topMarginOffsetInPx}px`,
              paddingBottom: `${bottomMarginOffsetInPx}px`,
            }}
          >
            {children}
          </div>
        </A4PageBackgroundContainer>
      </>
    )
  }
  return <>{children}</>
}

// OLD CODE FOR SHOWING BODY AND PRESCRIPTION SIZES FOR DEBUGGGING PURPOSES
/*
<div>
        <A4PreviewWrapper
          key={0}
          a4Width={a4PageWidth}
          showPageView={useNewPrintUI}
          heightOfOnePageInPixels={heightOfPageMeasureComponent}
          debugMode={debugMode}
        >
          <PMPConsultationParent
            ref={prescriptionParentDivRef}
            biggerFont={prescriptionData?.layout === PRESCRIPTION_LAYOUTS.largerFontLayout}
            noBorder={printType === PRESCRIPTION_PRINT_TYPES.withHeaderAndFooterBlankSpace}
            className={
              printType === PRESCRIPTION_PRINT_TYPES.withHeaderAndFooterBlankSpace
                ? ''
                : 'final-printable make-all-borders-black-print'
            }
            hiddenInUI={!showInUI}
          >
            {(headerStyleToShow === HEADER_TYPES?.onAllPages ||
              headerStyleToShow === HEADER_TYPES?.onFirstPage ||
              headerStyleToShow === HEADER_TYPES?.onAlternatePages) && (
              <>
                <PrescriptionHeader
                  prescriptionData={prescriptionData}
                  leaveSpace={printType === PRESCRIPTION_PRINT_TYPES.withHeaderAndFooterBlankSpace}
                  debug={debugMode}
                  printType={printType}
                  headerHeight={headerHeight}
                />
                {debugMode ? (
                  <div
                    style={{
                      position: 'absolute',
                      top: `${headerDimensions?.height - 10}px`,
                      right: '0px',
                    }}
                  >
                    HEADER ENDS: {headerDimensions?.height?.toFixed(2)} Px
                  </div>
                ) : (
                  <></>
                )}
              </>
            )}
            {headerStyleToShow === HEADER_TYPES?.blankHeader && (
              <>
                <PrescriptionHeader
                  prescriptionData={prescriptionData}
                  leaveSpace={true}
                  headerHeight={headerHeight}
                  debug={debugMode}
                  printType={printType}
                />
                {debugMode ? (
                  <div
                    style={{
                      position: 'absolute',
                      top: `${headerDimensions?.height - 10}px`,
                      right: '0px',
                    }}
                  >
                    HEADER ENDS AND BODY STARTS: {headerDimensions?.height?.toFixed(2)} Px
                  </div>
                ) : (
                  <></>
                )}
              </>
            )}
            {debugMode ? (
              prescriptionItemOrdersWithTopOffsets?.map((prescItem, idx) => (
                <div
                  style={{
                    position: 'absolute',
                    top: `${prescItem?.currentPosition - 10}px`,
                    left: '0px',
                  }}
                  key={`DEBUG${prescItem?.orderId}POSITION`}
                  id={`DEBUG${prescItem?.orderId}POSITION`}
                  onMouseEnter={(e) => {
                    onHoverItem({ e, prescItem })
                  }}
                  onMouseLeave={() => {
                    onMouseLeaveItem({ prescItem })
                  }}
                >
                  <Tooltip
                    title={getCoordinatesStringForPrescItems({
                      a4PageDimensions: a4PageDimensions,
                      item: prescItem,
                      prescriptionBreaks,
                    })}
                  >
                    <span>
                      {prescItem?.title}: <strong>Width:</strong>{' '}
                      {prescItem?.dimension?.width?.toFixed(2)}, <strong>Height:</strong>{' '}
                      {prescItem?.dimension?.height?.toFixed(2)}
                      <br></br>
                      <strong>Start:</strong> {prescItem?.currentPosition?.toFixed(2)} (Page:
                      {
                        getPageDetailsNumberOfAnItem({
                          footerHeight: footerDimensions?.height,
                          headerHeight: headerDimensions?.height,
                          itemInfo: prescItem,
                          pageHeight: heightOfPageMeasureComponent,
                          pageBreaks: prescriptionBreaks,
                        })?.[0]
                      }
                      ): <strong>End:</strong>{' '}
                      {(prescItem?.currentPosition + prescItem?.dimension?.height)?.toFixed(2)}{' '}
                      {getPageDetailsNumberOfAnItem({
                        footerHeight: footerDimensions?.height,
                        headerHeight: headerDimensions?.height,
                        itemInfo: prescItem,
                        pageHeight: heightOfPageMeasureComponent,
                        pageBreaks: prescriptionBreaks,
                      })?.[1]
                        ? `(Page: ${
                            getPageDetailsNumberOfAnItem({
                              footerHeight: footerDimensions?.height,
                              headerHeight: headerDimensions?.height,
                              itemInfo: prescItem,
                              pageHeight: heightOfPageMeasureComponent,
                              pageBreaks: prescriptionBreaks,
                            })?.[1]
                          })`
                        : `(Page: ${
                            getPageDetailsNumberOfAnItem({
                              footerHeight: footerDimensions?.height,
                              headerHeight: headerDimensions?.height,
                              itemInfo: prescItem,
                              pageHeight: heightOfPageMeasureComponent,
                              pageBreaks: prescriptionBreaks,
                            })?.[0]
                          })`}
                    </span>
                  </Tooltip>
                </div>
              ))
            ) : (
              <></>
            )}
            //<PrescriptionPrintableBody
            {debugMode ? (
              <div
                onMouseEnter={(e) => {
                  onHoverItem({
                    e,
                    prescItem: prescriptionBodyItemWithTopOffset,
                  })
                }}
                onMouseLeave={() => {
                  onMouseLeaveItem({
                    prescItem: prescriptionBodyItemWithTopOffset,
                  })
                }}
                style={{
                  position: 'absolute',
                  top: `${headerDimensions?.height + 10}px`,
                  right: '0px',
                }}
              >
                <Tooltip
                  title={getCoordinatesStringForPrescItems({
                    a4PageDimensions: a4PageDimensions,
                    item: prescriptionBodyItemWithTopOffset,
                    prescriptionBreaks,
                  })}
                >
                  Prescription Body Starts: {headerDimensions?.height?.toFixed(2)} Px
                </Tooltip>
              </div>
            ) : (
              <></>
            )}
            <PrescriptionFullBody
              value={value}
              setValue={setValue}
              placeholder={placeholder}
              alsoChangePropValue={Boolean(honorPropValue)}
              setPropValue={setPropValue}
              selectedAdvice={selectedAdvice}
              selectedTherapies={selectedTherapies}
              nextVisitInputType={nextVisitInputType}
              nextVisitInputValue={nextVisitInputValue}
              prescriptionData={prescriptionData}
              suggestedBookingTherapy={suggestedBookingTherapy}
              selectedInstructions={selectedInstructions}
              selectedInvestigations={selectedInvestigations} // todo: put in ViewOnlyPrescriptions - both type
              jointHomunculusData={jointHomunculusData}
              prescriptionViewSettings={prescriptionViewSettings}
              debug={debugMode}
              drugsAdvice={drugsAdvice}
              selectedObservations={selectedObservations}
              selectedComplaints={selectedComplaints}
              selectedDiagnosis={selectedDiagnosis}
              printType={printType}
              A4Height={heightOfPageMeasureComponent}
              footerHeight={footerDimensions?.height}
              headerHeight={headerDimensions?.height}
              debugHighlightStates={debugHighlightStates}
              practitionerId={practitionerId}
              signatureRef={signatureRef}
            />
            {debugMode ? (
              <div
                style={{
                  position: 'absolute',
                  // top: `${
                  //   heightOfPageMeasureComponent -
                  //   (headerDimensions?.height + footerDimensions?.height) -
                  //   10
                  // }px`,
                  top: `${
                    prescriptionParentDivDimensions?.height - footerDimensions?.height - 10
                  }px`,
                  right: '0px',
                }}
              >
                Prescirption Body ENDS and footer starts{' '}
                {(prescriptionParentDivDimensions?.height - footerDimensions?.height)?.toFixed(2)}{' '}
                px
              </div>
            ) : (
              <></>
            )}
            <PMPV2FooterContainer
              debug={debugMode}
              prescriptionWidth={prescriptionParentDivDimensions?.width}
            >
              <PrescriptionFooter
                prescriptionData={prescriptionData}
                leaveSpace={printType === PRESCRIPTION_PRINT_TYPES.withHeaderAndFooterBlankSpace}
                debug={debugMode}
                prescriptionWidth={prescriptionParentDivDimensions?.width}
              />
            </PMPV2FooterContainer>
          </PMPConsultationParent>
        </A4PreviewWrapper>
      </div>
      */
